import type { LayerSpecification } from 'maplibre-gl'
import {
  SLOT_BAND,
  SLOT_DOT,
  SLOT_HIGHLIGHT,
  SLOT_LABEL,
  SLOT_LINE,
  SLOT_RELIEF,
  SLOT_TINT,
} from '../../lib/map_config'

/**
 * Where an atlas layer goes in the stack: seven named layers that draw nothing
 * and exist to be inserted in front of.
 *
 * `map.addLayer(layer, beforeId)` puts a layer immediately under `beforeId`. So
 * a layer that names a slot lands at a fixed depth in the basemap without this
 * app knowing a single basemap layer id, and successive layers naming the same
 * slot stack in the order they were added.
 *
 * Two defects this replaced, one mechanism:
 *
 * - Every atlas fill used to be appended on top of the whole basemap, which is
 *   what read every road, river and town name through a film of wine-red and
 *   let a country polygon simplified to 22 km spill into the sea.
 * - Appellations are fetched after the first viewport report, so their layers
 *   were appended AFTER the winery dots and the journal's pins, and painted over
 *   them. A dot inside two regions rendered in the regions' colour, not its own.
 *
 * There were four. The plate is drawn on real ground now, and an appellation is
 * a hairline with a ribbon inside it rather than a film, so the stack has more
 * depths that mean something. Bottom to top:
 *
 * | Slot        | What goes under it                          | Sits                           |
 * | ----------- | ------------------------------------------- | ------------------------------ |
 * | `relief`    | the hillshade, once the map has drawn       | over the paper, under every    |
 * |             |                                             | ground cover                   |
 * | `tint`      | country tints and their edge marks; the     | over wood and town, under      |
 * |             | appellation fills a pointer is tested on    | vineyards, water and roads     |
 * | `band`      | the appellation ribbons, one layer          | over the vineyards, under      |
 * |             |                                             | water and roads                |
 * | `line`      | appellation hairlines                       | over roads and borders, under  |
 * |             |                                             | the selection and every label  |
 * | `highlight` | the selected place: the veil over what it   | over every hairline, under     |
 * |             | is not, and its amber ribbon and line; and, | every label                    |
 * |             | over those, the reader's own mark           |                                |
 * | `dot`       | the winery dots, and the room each keeps    | over every basemap label,      |
 * |             | clear of town names; the reader's keep-out  | under the appellation names    |
 * | `label`     | appellation names                           | over every basemap label and   |
 * |             |                                             | every dot                      |
 *
 * `line` moved. It sat under the roads, so that a road was not struck through
 * by a boundary. The ribbon is under the roads now, and it is the ribbon a road
 * cuts; the hairline is drawn over them, whole, so a boundary is one unbroken
 * line however many roads cross it. `line` and `highlight` are neighbours on
 * purpose: the veil has to cover every other appellation's hairline, whichever
 * arrives first, and two slots say that with no layer id written down.
 *
 * `band` is above the vineyards for the reason the old tint was below them,
 * reversed: a ribbon is the appellation's edge, and an edge under a sage block
 * would be cut wherever vines grow up to the boundary — which is where they
 * grow. Water still cuts it, so a boundary that follows a coast or a river is
 * trimmed by the surveyed line.
 *
 * `dot` is new, and it is the one place a winery layer names a slot. The dots
 * were appended with everything else of theirs, above the whole stack, which
 * put a dot on top of any name it shared ground with — and the name it most
 * often shares ground with is its own appellation's, the one a reader came for.
 * Under this slot a dot is still above every fill, ribbon and hairline (the
 * defect the slots exist for: a dot read through its regions' colour) and above
 * every basemap label, and an appellation's name is set over it. What a name
 * and a dot do about each other is then decided where labels are placed, not
 * by which is painted last: the dots' invisible keep-out layer is here too, so
 * a town's name steps aside for a dot and an appellation's does not
 * (winery_layer.ts).
 *
 * The reader's mark — where the browser says they are, once they have asked
 * (reader_mark.ts) — borrows two of these rather than getting an eighth. It is
 * added under `highlight` long after the selection was, so it lies over every
 * ribbon, hairline and veil and under every name, dot and pin: the reader is
 * on the ground, and what there is to read and to press is laid over them.
 * And it has a keep-out of its own under `dot`, for the reason the winery dots
 * have one: a reader is usually standing in a town.
 *
 * The cluster chips and the journal layers name no slot. They are appended, so
 * they are above all seven and therefore above every region layer whenever one
 * arrives.
 *
 * Both styles the map can be showing carry all seven, in this order — the
 * bundled atlas style and the plain fallback (lib/atlas_style.ts). That is not
 * optional: `addLayer` before a layer that does not exist fires an `error` and
 * adds NOTHING, so a style missing a slot silently loses every layer that names
 * it. One code path, and a test that holds both styles to it.
 *
 * The ids themselves are in lib/map_config.ts, beside every other layer id,
 * and are re-exported here so that everything in the map's chunk goes on
 * importing a slot from the file that explains it. They moved for the one
 * module that is NOT in this chunk and still names a slot: the reader's mark
 * (reader_mark.ts), which is fetched on its own, and which the bundler would
 * otherwise serve by cutting this file out into a third request on every map
 * page's boot.
 */
export { SLOT_BAND, SLOT_DOT, SLOT_HIGHLIGHT, SLOT_LABEL, SLOT_LINE, SLOT_RELIEF, SLOT_TINT }

export const ATLAS_SLOTS = [
  SLOT_RELIEF,
  SLOT_TINT,
  SLOT_BAND,
  SLOT_LINE,
  SLOT_HIGHLIGHT,
  SLOT_DOT,
  SLOT_LABEL,
] as const

// A `background` layer that is never visible: the one layer type that needs no
// source, so a slot can sit in a style that has none (the plain fallback).
export function slotLayer(id: string): LayerSpecification {
  return { id, type: 'background', layout: { visibility: 'none' } }
}
